import {
  FieldArrayPath, FieldArrayPathValue, FieldPath, FieldPathValue, FieldValues,
} from 'react-hook-form'
import { WithNullableId } from '../../models/types'

export type ArrayPathByValue<TForm extends FieldValues, TItem> = {
  [P in FieldArrayPath<TForm>]:
  FieldArrayPathValue<TForm, P> extends TItem[] ? P : never
}[FieldArrayPath<TForm>]

export function getValueByPath<
  TValues extends FieldValues,
  TPath extends FieldPath<TValues>,
>(
  values: TValues,
  path: TPath,
): FieldPathValue<TValues, TPath> {
  const tokens = path
    .replace(/\[(\d+)]/g, '.$1') // turn [0] into .0
    .split('.')
    .filter(Boolean)

  let current: any = values

  for (const token of tokens) {
    if (current == null) {
      return undefined as FieldPathValue<TValues, TPath>
    }
    current = current[token as keyof typeof current]
  }

  return current as FieldPathValue<TValues, TPath>
}

export function flattenFieldValues<TFieldValues extends FieldValues>(
  values: TFieldValues,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  function walk(current: any, prefix: string) {
    if (current == null) {
      result[prefix] = current
      return
    }

    if (Array.isArray(current)) {
      current.forEach((item, index) => {
        const path = prefix ? `${prefix}.${index}` : String(index)
        if (item !== null && typeof item === 'object') {
          walk(item, path)
        } else {
          result[path] = item
        }
      })
      return
    }

    if (typeof current === 'object') {
      Object.entries(current as Record<string, any>).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key
        if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
          walk(value, path)
        } else {
          result[path] = value
        }
      })
      return
    }

    // primitive at root
    if (prefix) {
      result[prefix] = current
    }
  }

  walk(values, '')

  return result
}

export type CreateForm<T extends WithNullableId> =
  T extends unknown
    ? Omit<T, 'id'> & { id: null }
    : never

export type UpdateForm<T extends WithNullableId> =
  T extends unknown
    ? Omit<T, 'id'> & { id: number }
    : never
